import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, database } from './firebase';
import { ref, get, push, set, onValue, update } from 'firebase/database';
import { useImpersonation } from './ImpersonationContext';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

function AdHocClockSelector() {
  const navigate = useNavigate();
  const { effectiveUserId } = useImpersonation();
  
  // Auth state
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  
  // Selection state
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingClock, setCreatingClock] = useState(null);
  
  // Clock state (after creation - becomes remote)
  const [clockId, setClockId] = useState(null);
  const [clockData, setClockData] = useState(null);
  const [remaining, setRemaining] = useState(45);
  const [isRunning, setIsRunning] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Refs
  const intervalRef = useRef(null);
  const wakeLockRef = useRef(null);
  const switchVersionRef = useRef(0);
  const cueScorePollRef = useRef(null); // interval for refreshing match list from CueScore
  
  const isMobile = window.innerWidth < 768;

  // Check auth
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      setAuthChecked(true);
      if (!currentUser) {
        navigate('/organiser-login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Wake lock for remote control
  useEffect(() => {
    if (!clockId) return;
    
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          console.log('🔆 Wake lock acquired (remote)');
        }
      } catch (err) {
        console.log('Wake lock error:', err);
      }
    };
    
    requestWakeLock();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clockId]);

  // Load tournaments
  useEffect(() => {
    if (!user) return;
    
    const loadTournaments = async () => {
      const userId = effectiveUserId || user.uid;
      const tournamentsRef = ref(database, `organisers/${userId}/tournaments`);
      
      try {
        const snapshot = await get(tournamentsRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          const tournamentList = Object.values(data)
            .filter(t => t.status === 'live' || t.status === 'upcoming')
            .sort((a, b) => new Date(b.starttime) - new Date(a.starttime));
          setTournaments(tournamentList);
        }
      } catch (error) {
        console.error('Error loading tournaments:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadTournaments();
  }, [user, effectiveUserId]);

  // Poll CueScore to keep match list fresh when a tournament is selected
  useEffect(() => {
    if (!selectedTournament?.tournamentId) {
      setMatches([]);
      if (cueScorePollRef.current) clearInterval(cueScorePollRef.current);
      return;
    }

    const refreshMatches = async () => {
      try {
        const response = await axios.get(
          `https://api.cuescore.com/tournament/?id=${selectedTournament.tournamentId}`
        );
        const allMatches = response.data.matches || [];
        const activeMatches = allMatches.filter(m =>
          m.matchstatus !== 'finished' &&
          m.matchstatusCode !== 2 &&
          m.playerA?.name &&
          m.playerB?.name
        );
        activeMatches.sort((a, b) => {
          const tableA = a.table?.name || a.tableNumber || 999;
          const tableB = b.table?.name || b.tableNumber || 999;
          if (tableA !== tableB) return tableA - tableB;
          return (a.matchno || 0) - (b.matchno || 0);
        });
        setMatches(activeMatches);
      } catch (err) {
        console.error('CueScore poll error:', err);
      }
    };

    // Fetch immediately then every 30 seconds
    refreshMatches();
    cueScorePollRef.current = setInterval(refreshMatches, 30000);

    return () => {
      if (cueScorePollRef.current) clearInterval(cueScorePollRef.current);
    };
  }, [selectedTournament?.tournamentId]);

  // Listen to clock data after creation
  useEffect(() => {
    if (!clockId) return;
    
    const clockRef = ref(database, `adHocClocks/${clockId}`);
    const unsubscribe = onValue(clockRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();

        // If session ended (e.g. display detected match finished), return to selector
        if (data.status === 'ended') {
          setClockId(null);
          setClockData(null);
          return;
        }

        setClockData(data);
        setRemaining(data.remaining);
        setIsRunning(data.isRunning);
        // Keep ref in sync with Firebase so rapid clicks always increment from latest value
        if (data.switchVersion && data.switchVersion > switchVersionRef.current) {
          switchVersionRef.current = data.switchVersion;
        }
      }
    });
    
    return () => unsubscribe();
  }, [clockId]);

  // Timer countdown (local mirror)
  useEffect(() => {
    if (isRunning && remaining > 0) {
      intervalRef.current = setInterval(() => {
        setRemaining(prev => Math.max(0, prev - 1));
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning]);

  // Create clock session
  const handleCreateClock = async (match) => {
    setCreatingClock(match.matchId);
    
    try {
      const userId = effectiveUserId || user.uid;
      const displayKey = Math.random().toString(36).substring(2, 10);
      
      const clockRef = push(ref(database, 'adHocClocks'));
      const newClockId = clockRef.key;
      
      await set(clockRef, {
        matchId: match.matchId,
        tournamentId: selectedTournament.tournamentId,
        tournamentName: selectedTournament.name,
        p1Name: match.playerA?.name || 'Player 1',
        p2Name: match.playerB?.name || 'Player 2',
        p1Image: match.playerA?.image || '',
        p2Image: match.playerB?.image || '',
        p1Frame: match.scoreA ?? 0,
        p2Frame: match.scoreB ?? 0,
        p1RunOuts: 0,
        p2RunOuts: 0,
        p1Active: true,
        duration: 45,
        extensionDuration: 15,
        remaining: 45,
        isRunning: false,
        p1ExtensionUsed: false,
        p2ExtensionUsed: false,
        tableNumber: match.table?.name || match.tableNumber || null,
        tableName: match.table?.venue?.name || '',
        displayKey: displayKey,
        displayLinked: false,
        displayId: null,
        organiserId: userId,
        createdAt: Date.now(),
        lastPoll: Date.now(),
        status: 'active'
      });
      
      setClockId(newClockId);
      
    } catch (error) {
      console.error('Error creating ad-hoc clock:', error);
      alert('Failed to create clock. Please try again.');
      setCreatingClock(null);
    }
  };

  // Remote control functions
  const handleSwitch = async () => {
    const newP1Active = !clockData.p1Active;
    switchVersionRef.current += 1;
    await update(ref(database, `adHocClocks/${clockId}`), {
      p1Active: newP1Active,
      isRunning: true,
      switchVersion: switchVersionRef.current
    });
  };

  const handleReset = async () => {
    await update(ref(database, `adHocClocks/${clockId}`), {
      remaining: clockData.duration,
      isRunning: true
    });
  };

  const handleExtension = async () => {
    const extensionField = clockData.p1Active ? 'p1ExtensionUsed' : 'p2ExtensionUsed';
    const alreadyUsed = clockData.p1Active ? clockData.p1ExtensionUsed : clockData.p2ExtensionUsed;
    
    if (alreadyUsed) return;
    
    const newRemaining = remaining + clockData.extensionDuration;
    await update(ref(database, `adHocClocks/${clockId}`), {
      remaining: newRemaining,
      [extensionField]: true
    });
  };

  const handlePauseResume = async () => {
    await update(ref(database, `adHocClocks/${clockId}`), {
      isRunning: !isRunning,
      remaining: remaining
    });
  };

  const handleSettingChange = async (field, value) => {
    const updates = { [field]: value };
    
    // If changing duration and clock is paused, also update remaining
    if (field === 'duration' && !isRunning) {
      updates.remaining = value;
    }
    
    await update(ref(database, `adHocClocks/${clockId}`), updates);
  };

  const handleEndSession = async () => {
    if (window.confirm('End this clock session?')) {
      await update(ref(database, `adHocClocks/${clockId}`), {
        status: 'ended',
        isRunning: false,
        displayLinked: false,
        displayId: null
      });
      setClockId(null);
      setClockData(null);
      setSelectedTournament(null);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (remaining <= 5) return '#ef4444';
    if (remaining <= 10) return '#f59e0b';
    return '#10b981';
  };

  const getQRUrl = () => {
    return `${window.location.origin}/adhoc-display?clock=${clockId}&key=${clockData?.displayKey || ''}`;
  };

  // Loading states
  if (!authChecked || loading) {
    return (
      <div style={styles.container}>
        <div style={styles.centerContent}>
          <p style={{ fontSize: '18px', color: '#8b95a8' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // REMOTE CONTROL VIEW (after clock created)
  if (clockId && clockData) {
    const activePlayer = clockData.p1Active ? clockData.p1Name : clockData.p2Name;
    const extensionUsed = clockData.p1Active ? clockData.p1ExtensionUsed : clockData.p2ExtensionUsed;

    return (
      <div style={styles.container}>
        {/* Controls at TOP */}
        <div style={styles.controlsSection}>
          <div style={styles.controlsGrid}>
            <button onClick={handleSwitch} style={styles.primaryButton}>
              🔄 SWITCH
            </button>
            <button onClick={handleReset} style={styles.primaryButton}>
              ↻ RESET
            </button>
          </div>
          
          <div style={styles.controlsGrid}>
            <button 
              onClick={handleExtension} 
              disabled={extensionUsed}
              style={{
                ...styles.secondaryButton,
                ...(extensionUsed && styles.disabledButton)
              }}
            >
              ⏱️ +{clockData.extensionDuration}s EXT
            </button>
            <button 
              onClick={handlePauseResume} 
              style={{
                ...styles.secondaryButton,
                backgroundColor: isRunning ? '#f59e0b' : '#10b981'
              }}
            >
              {isRunning ? '⏸️ PAUSE' : '▶️ START'}
            </button>
          </div>
        </div>

        {/* Timer Display */}
        <div style={styles.timerSection}>
          <div style={{ ...styles.timer, color: getTimerColor() }}>
            {formatTime(remaining)}
          </div>
        </div>

        {/* Match Info */}
        <div style={styles.matchSection}>
          <div style={styles.tableInfo}>
            {clockData.tableNumber && `Table ${clockData.tableNumber}`}
          </div>
          
          <div style={styles.playersRow}>
            <div style={{
              ...styles.playerName,
              ...(clockData.p1Active && styles.activePlayerName)
            }}>
              {clockData.p1Name}
              <div style={styles.runOutsSmall}>
                {Array(clockData.p1RunOuts || 0).fill().map((_, i) => (
                  <span key={i} style={styles.rooBadgeSmall}>RO</span>
                ))}
              </div>
            </div>
            
            <div style={styles.scoreSmall}>
              {clockData.p1Frame} - {clockData.p2Frame}
            </div>
            
            <div style={{
              ...styles.playerName,
              ...(!clockData.p1Active && styles.activePlayerName)
            }}>
              {clockData.p2Name}
              <div style={styles.runOutsSmall}>
                {Array(clockData.p2RunOuts || 0).fill().map((_, i) => (
                  <span key={i} style={styles.rooBadgeSmall}>RO</span>
                ))}
              </div>
            </div>
          </div>
          
          <div style={styles.activeLabel}>
            {activePlayer} shooting
            {extensionUsed && ' • Extension used'}
          </div>
        </div>

        {/* QR Code for Tablet */}
        <div style={styles.qrSection}>
          {clockData.displayLinked ? (
            <div style={styles.displayConnected}>
              <span style={styles.connectedIcon}>✓</span>
              <span>Display Connected</span>
            </div>
          ) : (
            <>
              <div style={styles.qrLabel}>📺 SCAN FROM TABLET</div>
              <div style={styles.qrCodeWrapper}>
                <QRCodeSVG
                  value={getQRUrl()}
                  size={150}
                  level="M"
                  bgColor="#1c2640"
                  fgColor="#ffffff"
                />
              </div>
              <div style={styles.codeDisplay}>
                Code: <span style={styles.codeText}>{clockId.replace(/^-/, '').substring(0, 6).toUpperCase()}</span>
              </div>
              <div style={styles.qrHint}>Go to /adhoc-display on tablet</div>
            </>
          )}
        </div>

        {/* Settings Toggle */}
        <div style={styles.settingsSection}>
          <button 
            onClick={() => setShowSettings(!showSettings)}
            style={styles.settingsToggle}
          >
            ⚙️ Settings {showSettings ? '▲' : '▼'}
          </button>
          
          {showSettings && (
            <div style={styles.settingsPanel}>
              <div style={styles.settingRow}>
                <span>Shot Clock</span>
                <div style={styles.settingButtons}>
                  {[30, 45, 60].map(val => (
                    <button
                      key={val}
                      onClick={() => handleSettingChange('duration', val)}
                      style={{
                        ...styles.settingButton,
                        ...(clockData.duration === val && styles.settingButtonActive)
                      }}
                    >
                      {val}s
                    </button>
                  ))}
                </div>
              </div>
              
              <div style={styles.settingRow}>
                <span>Extension</span>
                <div style={styles.settingButtons}>
                  {[10, 15, 20].map(val => (
                    <button
                      key={val}
                      onClick={() => handleSettingChange('extensionDuration', val)}
                      style={{
                        ...styles.settingButton,
                        ...(clockData.extensionDuration === val && styles.settingButtonActive)
                      }}
                    >
                      {val}s
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* End Session */}
        <button onClick={handleEndSession} style={styles.endButton}>
          End Clock Session
        </button>

        {/* Footer */}
        <div style={styles.footer}>
          <span style={styles.tournamentNameSmall}>{clockData.tournamentName}</span>
          <span style={styles.connectedBadge}>🟢 Remote Active</span>
        </div>
      </div>
    );
  }

  // SELECTION VIEW (before clock created)
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>⏱️ Ad-Hoc Shot Clock</h1>
          <p style={{ color: '#8b95a8', marginTop: '4px' }}>
            Select a live match to add a shot clock
          </p>
        </div>
        <button onClick={() => window.close()} style={styles.backButton}>
          ✕ Close
        </button>
      </div>

      <div style={styles.content}>
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
            1. Select Tournament
          </h2>
          
          {tournaments.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No active tournaments found.</p>
              <p style={{ marginTop: '8px', fontSize: '14px' }}>
                Sync your CueScore tournaments from the dashboard first.
              </p>
            </div>
          ) : (
            <select
              value={selectedTournament?.tournamentId || ''}
              onChange={(e) => {
                const tournament = tournaments.find(t => 
                  t.tournamentId.toString() === e.target.value
                );
                setSelectedTournament(tournament || null);
              }}
              style={styles.select}
            >
              <option value="">Choose a tournament...</option>
              {tournaments.map(t => (
                <option key={t.tournamentId} value={t.tournamentId}>
                  {t.name} ({t.status})
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedTournament && (
          <div style={styles.card}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
              2. Select Match
            </h2>
            
            {matches.length === 0 ? (
              <div style={styles.emptyState}>
                <p>No active matches found in this tournament.</p>
              </div>
            ) : (
              <div>
                {matches.map(match => {
                  const isCreating = creatingClock === match.matchId;
                  const p1Frames = match.scoreA ?? 0;
                  const p2Frames = match.scoreB ?? 0;
                  
                  return (
                    <div key={match.matchId} style={styles.matchCard}>
                      <div style={styles.matchInfo}>
                        <div style={styles.playerNames}>
                          {match.playerA?.name} vs {match.playerB?.name}
                        </div>
                        <div style={styles.matchMeta}>
                          {match.table?.name && `Table ${match.table.name} • `}
                          {match.roundName || 'Round'}
                        </div>
                      </div>
                      
                      <div style={styles.score}>
                        {p1Frames} - {p2Frames}
                      </div>
                      
                      <button
                        onClick={() => handleCreateClock(match)}
                        disabled={isCreating}
                        style={{
                          ...styles.startButton,
                          ...(isCreating && styles.disabledButton)
                        }}
                      >
                        {isCreating ? '⏳...' : '⏱️ Start'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div style={styles.helpBox}>
          <p style={{ color: '#8b95a8', fontSize: '14px', lineHeight: '1.6' }}>
            <strong>💡 How it works:</strong><br />
            1. Select a match above<br />
            2. This phone becomes the remote control<br />
            3. Scan the QR code from a tablet to show the clock display<br />
            4. Control the clock from this phone
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0d1321',
    color: '#f0f2f5',
    padding: '16px',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '430px',
    margin: '0 auto'
  },
  centerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh'
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '24px',
    gap: '16px'
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    fontFamily: "'Outfit', sans-serif"
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: '#1c2640',
    color: '#8b95a8',
    border: '1px solid #2a3555',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  content: {
    flex: 1
  },
  card: {
    backgroundColor: '#151c2e',
    borderRadius: '14px',
    padding: '20px',
    marginBottom: '16px',
    border: '1px solid #2a3555'
  },
  select: {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: '#1c2640',
    color: 'white',
    border: '1px solid #2a3555',
    borderRadius: '10px',
    fontSize: '16px',
    cursor: 'pointer'
  },
  matchCard: {
    backgroundColor: '#1c2640',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '10px',
    border: '1px solid #2a3555',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  matchInfo: {
    flex: 1,
    minWidth: 0
  },
  playerNames: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  matchMeta: {
    fontSize: '12px',
    color: '#8b95a8'
  },
  score: {
    fontSize: '20px',
    fontWeight: 'bold',
    padding: '0 8px'
  },
  startButton: {
    padding: '10px 16px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  },
  emptyState: {
    textAlign: 'center',
    padding: '24px',
    color: '#6b7280'
  },
  helpBox: {
    backgroundColor: '#1c2640',
    borderRadius: '10px',
    padding: '16px'
  },
  // Remote control styles
  controlsSection: {
    marginBottom: '16px'
  },
  controlsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginBottom: '10px'
  },
  primaryButton: {
    padding: '18px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif"
  },
  secondaryButton: {
    padding: '14px',
    backgroundColor: '#1c2640',
    color: 'white',
    border: '2px solid #2a3555',
    borderRadius: '14px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif"
  },
  disabledButton: {
    opacity: 0.4,
    cursor: 'not-allowed'
  },
  timerSection: {
    textAlign: 'center',
    marginBottom: '16px'
  },
  timer: {
    fontSize: '72px',
    fontWeight: '800',
    fontFamily: "'Outfit', sans-serif",
    lineHeight: 1
  },
  matchSection: {
    backgroundColor: '#151c2e',
    borderRadius: '16px',
    padding: '14px',
    marginBottom: '12px',
    textAlign: 'center'
  },
  tableInfo: {
    fontSize: '12px',
    color: '#8b95a8',
    marginBottom: '8px'
  },
  playersRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px'
  },
  playerName: {
    fontSize: '14px',
    fontWeight: '600',
    flex: 1,
    textAlign: 'center',
    padding: '6px',
    borderRadius: '8px'
  },
  activePlayerName: {
    backgroundColor: '#10b98120',
    color: '#10b981'
  },
  scoreSmall: {
    fontSize: '24px',
    fontWeight: '800',
    padding: '0 12px',
    fontFamily: "'Outfit', sans-serif"
  },
  runOutsSmall: {
    display: 'flex',
    gap: '3px',
    justifyContent: 'center',
    marginTop: '3px'
  },
  rooBadgeSmall: {
    padding: '2px 5px',
    backgroundColor: '#10b981',
    color: 'white',
    borderRadius: '3px',
    fontSize: '9px',
    fontWeight: '700'
  },
  activeLabel: {
    fontSize: '12px',
    color: '#8b95a8'
  },
  qrSection: {
    backgroundColor: '#1c2640',
    borderRadius: '14px',
    padding: '16px',
    textAlign: 'center',
    marginBottom: '12px'
  },
  qrLabel: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#8b95a8'
  },
  qrCodeWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '8px'
  },
  qrHint: {
    fontSize: '11px',
    color: '#6b7280'
  },
  codeDisplay: {
    fontSize: '14px',
    color: '#8b95a8',
    marginBottom: '8px'
  },
  codeText: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#10b981',
    letterSpacing: '2px'
  },
  displayConnected: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontSize: '18px',
    color: '#10b981',
    fontWeight: '600',
    padding: '8px 0'
  },
  connectedIcon: {
    fontSize: '24px'
  },
  settingsSection: {
    marginBottom: '12px'
  },
  settingsToggle: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#1c2640',
    color: '#8b95a8',
    border: '1px solid #2a3555',
    borderRadius: '10px',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif"
  },
  settingsPanel: {
    backgroundColor: '#151c2e',
    borderRadius: '12px',
    padding: '14px',
    marginTop: '8px'
  },
  settingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
    fontSize: '14px'
  },
  settingButtons: {
    display: 'flex',
    gap: '6px'
  },
  settingButton: {
    padding: '6px 14px',
    backgroundColor: '#1c2640',
    color: '#8b95a8',
    border: '1px solid #2a3555',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif"
  },
  settingButtonActive: {
    backgroundColor: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6'
  },
  endButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    color: '#ef4444',
    border: '1px solid #ef4444',
    borderRadius: '10px',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    marginBottom: '12px'
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderTop: '1px solid #2a3555',
    marginTop: 'auto'
  },
  tournamentNameSmall: {
    fontSize: '11px',
    color: '#6b7280',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  connectedBadge: {
    fontSize: '11px',
    color: '#10b981'
  }
};

export default AdHocClockSelector;